/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {LoadingButton} from '@mui/lab';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import {useTranslation} from 'react-i18next';
import {publicService} from "../../../../../api/services/public";
import * as Yup from "yup";
import {Field, FieldProps, Formik, FormikProps} from "formik";
import {Message} from "../../../../../types/common";
import Card from "@mui/material/Card";
import {formValidation} from "../../../../../utils/formValidation";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useSnack} from "../../../../../utils/useSnack";
import {FormikHelpers} from "formik/dist/types";

const Form = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const {t} = useTranslation();

  const formSchema = Yup.object({
    lastName: Yup.string().nullable(),
    firstName: Yup.string().nullable(),
    itn: Yup.string().required(t("validation.itn_required")),
    email: Yup.string().required(t("validation.email_required"))
        .email(t("validation.email_format")),
    message: Yup.string().required(t("validation.required").replace('{max}', '1000'))
  })

  const initialValues: Message = {
    lastName: null,
    firstName: null,
    itn: '',
    email: '',
    message: '',
  }
  const [loading, setLoading] = React.useState(false);
  const commonFormikRef = React.useRef<FormikProps<Message>>(null);
  const {snack} = useSnack()
  const {executeRecaptcha} = useGoogleReCaptcha();
  const onSubmit = async (values: Message, formikHelpers: FormikHelpers<Message>) => {
  }

  const submit = async () => {
    setLoading(true);
    const isValid = await formValidation(commonFormikRef)
    if (!commonFormikRef?.current?.isValid) {
      setLoading(false)
      return
    }
    try {
      const recaptchaToken = await executeRecaptcha?.("login");
      const response = await publicService.sendMessage(commonFormikRef!.current!.values, recaptchaToken!)

      if (response.status === 'success') {
        snack(t("contacts_page.message_send"), 'success')
        commonFormikRef.current.resetForm()
      }
    } catch (e) {
      snack(t("contacts_page.message_not_sent"), 'error')
    } finally {
      setLoading(false);
    }
  }
  return (
      <Box>
        <Box
            padding={{xs: 3, sm: 6}}
            width={'100%'}
            component={Card}
            borderRadius={2}
            boxShadow={4}
            marginBottom={4}
        >
          <Formik innerRef={commonFormikRef}
                  initialValues={initialValues}
                  validationSchema={formSchema}
                  onSubmit={onSubmit}
                  validateOnMount>
            {(frops: FormikProps<Message>) => (
                <Grid container spacing={isMd ? 4 : 2}>
                  <Grid item xs={12} sm={6}>
                    <Field name={"firstName"}>
                      {({field, form, meta}: FieldProps) => (
                          <TextField
                              {...field}
                              sx={{height: 54}}
                              label={t("inputs.first_name")}
                              variant="outlined"
                              color="primary"
                              size="medium"
                              fullWidth
                              error={!!(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                          />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name={"lastName"}>
                      {({field, form, meta}: FieldProps) => (
                          <TextField
                              {...field}
                              sx={{height: 54}}
                              label={t("inputs.last_name")}
                              variant="outlined"
                              color="primary"
                              size="medium"
                              error={!!(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                          />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sx={{paddingBottom: 2}}>

                    <Field name={"email"}>
                      {({field, form, meta}: FieldProps) => (
                          <TextField
                              {...field}
                              sx={{height: 54}}
                              label={t("inputs.email")}
                              type="email"
                              variant="outlined"
                              color="primary"
                              size="medium"
                              fullWidth
                              error={!!(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                          />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sx={{paddingBottom: 2}}>

                    <Field name={"itn"}>
                      {({field, form, meta}: FieldProps) => (
                          <TextField
                              {...field}
                              sx={{height: 54}}
                              label={t("inputs.itn")}
                              variant="outlined"
                              color="primary"
                              size="medium"
                              fullWidth
                              error={!!(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                          />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sx={{paddingBottom: 2}}>

                    <Field name={"message"}>
                      {({field, form, meta}: FieldProps) => (
                          <TextField
                              {...field}
                              label={t("inputs.message")}
                              multiline
                              rows={6}
                              variant="outlined"
                              color="primary"
                              size="medium"
                              fullWidth
                              error={!!(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                          />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <LoadingButton
                        sx={{height: 54}}
                        variant="contained"
                        color="primary"
                        size="medium"
                        fullWidth
                        onClick={submit}
                        loading={loading}
                    >
                      {t("contacts_page.send")}
                    </LoadingButton>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider/>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography component="p" variant="body2" align="left">
                        {t("contacts_page.by_clicking_on_button")} "{t("contacts_page.send")}", {t("contacts_page.you_agree_with_ours")}{' '}
                        <Box
                            component="a"
                            href=""
                            color={theme.palette.text.primary}
                            fontWeight={'700'}
                        >
                          {t("contacts_page.privacy_policy")}
                        </Box>
                        ,{' '}
                        <Box
                            component="a"
                            href=""
                            color={theme.palette.text.primary}
                            fontWeight={'700'}
                        >
                          {t("contacts_page.data_policy")}
                        </Box>{' '}
                        .
                      </Typography>
                    </Box>

                  </Grid>
                </Grid>
            )}
          </Formik>
        </Box>
        <Box>
          <Typography color="text.secondary" align={'center'}>
            Мы ответим в течение 1-2 рабочих дней.
          </Typography>
        </Box>
      </Box>)

}

export default Form;
